import React from "react";
import NextImage from "../../common/NextImage";
import MatchScores from "../../common/football/MatchScores";
import MatchMinutes from "../../common/MatchMinutes";
import MatchDateTime from "../../common/MatchDateTime";
import { MatchOptions } from "../../../utils/matchOptions";
import {
  renderStatusTag,
  convertDateDMY,
  navigateToMatch,
  getMatchOutcomeClass,
  getImageUrl,
  getMatchOutcome,
} from "../../../utils/generalLiveCenter";

const LeagueBody = ({
  match,
  matchType,
  updatedData,
  difference,
  Result,
  sport = "football",
  websiteBaseUrl,
  dataClient,
  dataPath,
  teamid,
  design,
  dictionary = {},
}) => {
  if (!match?.competitors) return null;

  const showTime =
    matchType === "upcoming" &&
    !["postponed", "abandoned", "cancelled"].includes(match?.st?.toLowerCase());

  let score = MatchOptions?.footballFTStatuses?.some(
    (status) => status.toLowerCase() === match?.st?.toLowerCase()
  )
    ? match?.ft
    : match?.cfs;

  let winner = score;
  if (match?.mins === "FAP") {
    winner = match?.ps;
  }

  // Check if match is live (case-insensitive)
  const isLive = MatchOptions?.footballLiveStatuses?.some(
    (status) => status.toLowerCase() === match?.st?.toLowerCase()
  );

  // Check if match is finished (case-insensitive)
  const isFinished = MatchOptions?.footballFTStatuses?.some(
    (status) => status.toLowerCase() === match?.st?.toLowerCase()
  );

  // Render status badge based on match status
  const renderStatus = () => {
    const status = match?.st?.toLowerCase();

    if (status === "delayed") {
      return <span className="sts_tg">Dly</span>;
    }
    if (status === "postponed") {
      return <span className="sts_tg ppd">PPD</span>;
    }
    if (status === "nsy" && matchType === "upcoming") {
      return null; // Time will be shown separately
    }
    if (status === "nsy") {
      return <span className="sts_tg">NSY</span>;
    }
    if (status === "abandoned") {
      return <span className="sts_tg red">AB</span>;
    }
    if (status === "cancelled") {
      return <span className="sts_tg red">CAN</span>;
    }
    if (status === "result only") {
      return <span className="sts_tg ppd">Result only</span>;
    }
    if (status === "finished" && match?.mins === "To Finish") {
      return <span className="status">To FT</span>;
    }
    if (status === "finished" && match?.mins === "FAP") {
      return <span className="sts_tg red">FAP</span>;
    }
    if (status === "tf") {
      return <span className="sts_tg red">TF</span>;
    }
    if (status === "finished" && match?.mins !== "FAP" && match?.mins !== "FAET") {
      return (
        <span className="sts_tg">
          {sport === "rugby" || sport === "rugby-union" ? "Finished" : "FT"}
        </span>
      );
    }
    if (status === "finished" && match?.mins === "FAET") {
      return <span className="sts_tg">FAET</span>;
    }
    if (status === "finished ap") {
      return <span className="sts_tg">FAP</span>;
    }
    if (status === "finished aet") {
      return <span className="sts_tg">FAET</span>;
    }

    return renderStatusTag(match);
  };

  return (
    <div className="leg_body" key={match?.id}>
      <div
        className={`leg_row rugby_leg_row ${isFinished ? "finesh" : ""} ${isLive ? "live" : ""}`}
        onClick={() => navigateToMatch(match, dataClient, websiteBaseUrl, dataPath)}
        style={{ cursor: "pointer" }}
      >
        <span className="match_list_date_status">
          <span className="status_wrp">
            {renderStatus()}

            {/* Live status indicator */}
            {isLive && (
              <div className="live_sts">
                <span className="sts_tg redd">
                  <span className="sts_m_min">
                    <MatchMinutes match={match} />
                  </span>
                  <span className="sts_m_live">{dictionary?.live || "Live"}</span>
                </span>
              </div>
            )}

            {/* Date display for specific conditions */}
            {sport !== "rugby" && sport !== "rugby-union" && (
              <>
                {(showTime || updatedData || isFinished) && (
                  <span className="date">{convertDateDMY(match?.dt)}</span>
                )}
              </>
            )}

            {/* Time display */}
            {(match?.st === "NSY" || match?.st === "Result Only") && (
              <MatchDateTime match={match} sport={sport} difference={difference} />
            )}

            {/* Rugby always shows date and time */}
            {(sport === "rugby" || sport === "rugby-union") && (
              <>
                <span className="date">{convertDateDMY(match?.dt)}</span>
                <MatchDateTime match={match} sport={sport} difference={difference} />
              </>
            )}
          </span>
        </span>

        <div className={`leg_inf_inner ${Result ? "result_active" : ""}`}>
          <div className="leg_inf">
            {/* Home Team */}
            <div className="cl_team">
              <span className="flag">
                <NextImage
                  src={getImageUrl("", "", sport, match?.competitors?.htid)}
                  height={23}
                  width={23}
                  alt={`${match?.competitors?.htn || "Home team"} logo`}
                />
              </span>
              <span className="team_name">
                <span
                  className={`name ${
                    winner?.split("-")[0] > winner?.split("-")[1]
                      ? "font-weight-bold"
                      : ""
                  }`}
                >
                  {match?.competitors?.htn}
                </span>
              </span>
            </div>

            {/* Score */}
            <div className="cl_score">
              {["NSY", "Delayed", "Result Only"].includes(match?.st) ? (
                <>
                  <span className="score">-</span>
                  <span className="score">-</span>
                </>
              ) : (
                <MatchScores
                  score={score}
                  key={`${match?.id}-score`}
                  winner={winner}
                />
              )}
            </div>

            {/* Away Team */}
            <div className="cl_team snd_team pr_p">
              <span className="flag">
                <NextImage
                  src={getImageUrl("", "", sport, match?.competitors?.atid)}
                  height={23}
                  width={23}
                  alt={`${match?.competitors?.atn || "Away team"} logo`}
                />
              </span>
              <span className="team_name">
                <span
                  className={`name ${
                    winner?.split("-")[1] > winner?.split("-")[0]
                      ? "font-weight-bold"
                      : ""
                  }`}
                >
                  {match?.competitors?.atn}
                </span>
              </span>
            </div>
          </div>

          {/* Win/Draw/Loss indicator for team results */}
          {Result && match?.cp !== "Postponed" && (
            <div className="win-td">
              <div className="icon-wrap">
                <span className={`icon_status ${getMatchOutcomeClass(teamid, match)}`}>
                  {getMatchOutcome(teamid, match)}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeagueBody;
